import { useState, useEffect, useRef } from "react";

// Toggle ("All sensors on this device" vs "Choose specific sensors") + conditional
// checklist, controlled from outside via sensorMode/sensorIds + onChange({sensor_mode,
// sensor_ids}). No ui/ primitive fits a toggle+checklist combo, so this is hand-rolled
// plain markup, matching this project's "leave raw when no primitive fits" convention.
//
// Checklist selections are kept in local state independent of the toggle position —
// switching "All sensors" -> "Choose specific sensors" -> back never discards
// in-progress picks. Only the parent's onChange (fired on every change, committed by
// the caller's own Save action) or navigating away unsaved discards anything. This
// matches the resolved design in infra/.planning/proposals/sensor-assignments.md
// (committee finding #2, pass 2).
export default function SensorAssignmentPicker({ sensors, sensorMode, sensorIds, onChange, disabled = false }) {
  const [localIds, setLocalIds] = useState(sensorIds ?? []);
  // Tracks whether the caller has ever handed us a real saved value in custom mode
  // (e.g. pre-hydration from an existing phase's GET). Before that, sensorIds is just
  // this component's own last onChange echoed back — re-syncing from it would fight
  // the toggle-independence guarantee below.
  const hydratedFromCustom = useRef(sensorMode === "custom");

  // Re-sync local checklist state only the first time the caller provides a real saved
  // custom-mode value (pre-hydration). Deliberately does NOT re-sync on every sensorMode/
  // sensorIds change — switching to "All sensors" and back must never discard in-progress
  // checklist picks (resolved design requirement, committee finding #2 pass 2). The
  // parent's onChange always receives sensor_ids: [] when mode is device_wide, so re-
  // syncing off that value would silently clear the user's picks.
  useEffect(() => {
    if (sensorMode === "custom" && !hydratedFromCustom.current) {
      setLocalIds(sensorIds ?? []);
      hydratedFromCustom.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensorMode, JSON.stringify(sensorIds)]);

  function setMode(mode) {
    if (mode === "device_wide") {
      onChange({ sensor_mode: "device_wide", sensor_ids: [] });
    } else {
      onChange({ sensor_mode: "custom", sensor_ids: localIds });
    }
  }

  function toggleSensor(id) {
    const next = localIds.includes(id) ? localIds.filter(x => x !== id) : [...localIds, id];
    setLocalIds(next);
    onChange({ sensor_mode: "custom", sensor_ids: next });
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setMode("device_wide")}
          className={`flex-1 text-sm rounded-lg border px-3 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            sensorMode === "device_wide"
              ? "border-sky-500 bg-sky-50 dark:bg-sky-950 text-sky-800 dark:text-sky-300 font-medium"
              : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          }`}
        >
          All sensors on this device
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setMode("custom")}
          className={`flex-1 text-sm rounded-lg border px-3 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            sensorMode === "custom"
              ? "border-sky-500 bg-sky-50 dark:bg-sky-950 text-sky-800 dark:text-sky-300 font-medium"
              : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          }`}
        >
          Choose specific sensors
        </button>
      </div>

      {sensorMode === "custom" && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-600 divide-y divide-gray-100 dark:divide-gray-700">
          {sensors.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 px-3 py-2">
              No sensors reporting on this device yet.
            </p>
          ) : (
            sensors.map((s) => (
              <label
                key={s.id}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer"
              >
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={localIds.includes(s.id)}
                  onChange={() => toggleSensor(s.id)}
                  className="rounded border-gray-300 dark:border-gray-600 text-sky-600 focus:ring-sky-500"
                />
                <span>{s.display_name || s.sensor_name || s.sensor_code}</span>
                <span className="text-xs font-mono text-gray-400 dark:text-gray-500">{s.sensor_code}</span>
              </label>
            ))
          )}
          {localIds.length === 0 && sensors.length > 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500 px-3 py-2">
              Tracking no sensors — pick some above, or switch to &ldquo;All sensors on this device&rdquo;.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
